'use client';

import { Breadcrumb } from '@/components/common/breadcrumb';
import { menuBreadcrumbItems } from '@/config/menu-catalog';
import { PageHeader } from '@/components/common/page-header';
import { ExpandableCard } from '@/components/ui/expandable-card';
import { useToast } from '@/hooks/use-toast';
import { apiClient } from '@/lib/api-client';
import { GyeProjectionDto } from '@/lib/api/data-contracts';
import { Master } from '@/lib/api/MasterRoute';
import { useCallback, useEffect, useState } from 'react';
import * as RealGrid from 'realgrid';
import { RealGridReact, RGDataColumn, RGDataField } from 'realgrid-react';

export function AccountManagement() {
  const { toast } = useToast();
  const [accounts, setAccounts] = useState<GyeProjectionDto[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchAccounts = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiClient.get('master/account');
      const data =
        (await response.json()) as Master.GetAccountData.ResponseBody;
      const accountData = (data.result as GyeProjectionDto[]) || [];
      setAccounts(accountData);
    } catch {
      toast({
        title: '조회 실패',
        description: '계정 과목을 불러오는 중 오류가 발생했습니다',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  // 초기 데이터 로드
  useEffect(() => {
    fetchAccounts();
  }, []);

  // 조회 버튼 클릭 핸들러
  const handleSearch = () => {
    fetchAccounts();
  };

  return (
    <ExpandableCard
      breadcrumb={
        <Breadcrumb items={menuBreadcrumbItems('account')} />
      }
      title='계정 과목'
      fill
      pageHeader={
        <PageHeader
          onSearch={handleSearch}
          isLoading={isLoading}
        />
      }
    >
      {/* 계정 테이블 */}
      <div
        className='border rounded-lg flex-1 min-h-0'
      >
        <div className='w-full h-full'>
          <RealGridReact
            gridProps={{
              checkBar: { visible: false },
              stateBar: { visible: false },
              edit: { readOnly: true, editable: false },
              sorting: {
                enabled: true,
                showSortOrder: true,
                style: RealGrid.SortStyle.INCLUSIVE,
              },
            }}
            rows={accounts}
          >
            <RGDataField
              fieldName={'companyCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'companyName'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'storeCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'storeName'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'gyeCode'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'gyeName'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataField
              fieldName={'gyeCheck'}
              dataType={RealGrid.ValueType.TEXT}
            />
            <RGDataColumn
              fieldName={'storeName'}
              header={{ text: '주유소명' }}
              mergeRule={'value'}
            />
            <RGDataColumn
              fieldName={'gyeCode'}
              header={{ text: '계정과목 코드' }}
            />
            <RGDataColumn
              fieldName={'gyeName'}
              header={{ text: '계정과목명' }}
              mergeRule={'value'}
            />
            <RGDataColumn
              fieldName={'gyeCheck'}
              header={{ text: '사용여부' }}
              lookupDisplay={true}
              values={['0', '1']}
              labels={['사용', '미사용']}
            />
          </RealGridReact>
        </div>
      </div>
    </ExpandableCard>
  );
}
